import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Button, Modal, Select, Radio, Alert, message, Spin, Tag, Space } from 'antd';
import { ThunderboltOutlined, DatabaseOutlined, CloudOutlined, DollarOutlined } from '@ant-design/icons';
import axios from 'axios';
import logger from '../../utils/logger';
import { getApiErrorMessage } from '../../utils/apiError';

const { Option } = Select;

const TenantComputeSettings = ({ tenant, onRefresh }) => {
  const [rdsStatus, setRdsStatus] = useState(null);
  const [loadingRdsStatus, setLoadingRdsStatus] = useState(false);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [selectedInstanceClass, setSelectedInstanceClass] = useState(null);
  const [applyImmediately, setApplyImmediately] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  const getAuthHeader = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?.access_token ? { Authorization: `Bearer ${user.access_token}` } : {};
  };

  const API_URL = import.meta.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Fetch RDS status when component mounts
  useEffect(() => {
    if (tenant && tenant.tier === 'client' && tenant.rds_endpoint) {
      fetchRdsStatus();
    }
  }, [tenant]);

  const fetchRdsStatus = async () => {
    setLoadingRdsStatus(true);
    try {
      const response = await axios.get(
        `${API_URL}/admin/tenants/${tenant.tenant_id}/rds-status`,
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        setRdsStatus(response.data);
      } else {
        logger.error('Failed to fetch RDS status:', response.data.error);
      }
    } catch (error) {
      logger.error('Error fetching RDS status:', error);
    } finally {
      setLoadingRdsStatus(false);
    }
  };

  const handleUpgradeRds = async () => {
    if (!selectedInstanceClass) {
      message.error('Please select a target instance class');
      return;
    }

    setUpgrading(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/tenants/${tenant.tenant_id}/upgrade-rds`,
        {
          new_instance_class: selectedInstanceClass,
          apply_immediately: applyImmediately
        },
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        message.success(
          `RDS upgrade initiated! ${applyImmediately ? 'Will complete in 10-15 minutes.' : 'Will be applied during next maintenance window.'}`,
          10
        );
        setUpgradeModalVisible(false);
        setSelectedInstanceClass(null);
        setApplyImmediately(false);

        // Refresh tenant data and RDS status
        if (onRefresh) onRefresh();
        setTimeout(() => fetchRdsStatus(), 2000);
      } else {
        message.error(response.data.error || response.data.message || 'Failed to upgrade RDS');
      }
    } catch (error) {
      logger.error('Error upgrading RDS:', error);
      message.error(getApiErrorMessage(error, 'Failed to upgrade RDS. Please try again.'));
    } finally {
      setUpgrading(false);
    }
  };

  const getCostEstimate = (instanceClass) => {
    const costs = {
      'db.t3.micro': '$15',
      'db.t3.small': '$30',
      'db.t3.medium': '$60'
    };
    return costs[instanceClass] || 'N/A';
  };

  const getTierDisplayName = (tier) => {
    const names = {
      'tier-1': 'Tier 1 (Starter)',
      'tier-2': 'Tier 2 (Standard)',
      'tier-3': 'Tier 3 (Premium)'
    };
    return names[tier] || tier;
  };

  const getStatusTag = (status) => {
    const statusMap = {
      'available': <Tag color="green">Available</Tag>,
      'modifying': <Tag color="orange">Modifying</Tag>,
      'upgrading': <Tag color="blue">Upgrading</Tag>,
      'RDS_UPGRADING': <Tag color="blue">Upgrading</Tag>,
      'PROVISIONING': <Tag color="processing">Provisioning</Tag>,
      'READY': <Tag color="success">Ready</Tag>
    };
    return statusMap[status] || <Tag>{status}</Tag>;
  };

  // Training tenant - show shared infrastructure info
  if (!tenant || tenant.tier === 'training') {
    return (
      <Card
        title={<Space><ThunderboltOutlined /> Compute Configuration</Space>}
        style={{ marginTop: 16 }}
      >
        <Alert
          message="Shared Infrastructure"
          description="This training tenant uses shared infrastructure (Platform RDS, shared S3, shared ECS). No compute tier configuration available."
          type="info"
          showIcon
        />
        <Descriptions column={1} style={{ marginTop: 16 }}>
          <Descriptions.Item label="Tenant Class">Training</Descriptions.Item>
          <Descriptions.Item label="Infrastructure">Shared (Platform RDS + shared ECS cluster)</Descriptions.Item>
          <Descriptions.Item label="Estimated Cost">~$5/month</Descriptions.Item>
        </Descriptions>
      </Card>
    );
  }

  // Client tenant - show compute tier and upgrade options
  return (
    <Card
      title={<Space><ThunderboltOutlined /> Compute Configuration</Space>}
      style={{ marginTop: 16 }}
      extra={
        rdsStatus?.available_upgrades?.length > 0 && (
          <Button
            type="primary"
            icon={<DatabaseOutlined />}
            onClick={() => setUpgradeModalVisible(true)}
            disabled={rdsStatus?.is_upgrading || tenant.status === 'PROVISIONING'}
          >
            Upgrade RDS
          </Button>
        )
      }
    >
      {tenant.status === 'PROVISIONING' && (
        <Alert
          message="Provisioning in Progress"
          description="This tenant is currently being provisioned. Compute configuration will be available once provisioning is complete (~30 minutes)."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Descriptions column={2} bordered>
        <Descriptions.Item label="Tenant Class" span={2}>
          <Space>
            Client (Dedicated Infrastructure)
            {getStatusTag(tenant.status)}
          </Space>
        </Descriptions.Item>

        <Descriptions.Item label="Compute Tier">
          {tenant.compute_tier ? getTierDisplayName(tenant.compute_tier) : 'N/A'}
        </Descriptions.Item>

        <Descriptions.Item label="Estimated Cost">
          {tenant.compute_tier === 'tier-1' && '$58/month'}
          {tenant.compute_tier === 'tier-2' && '$144/month'}
          {tenant.compute_tier === 'tier-3' && '$298/month'}
          {!tenant.compute_tier && 'N/A'}
        </Descriptions.Item>

        <Descriptions.Item label={<Space><CloudOutlined /> ECS Configuration</Space>} span={2}>
          {tenant.ecs_service_arn ? (
            <Space direction="vertical" size="small">
              <div>
                <strong>Tasks:</strong> {tenant.ecs_min_tasks || 1}-{tenant.ecs_max_tasks || 2} (auto-scaling)
              </div>
              <div style={{ fontSize: '12px', color: 'var(--rlc-neutral-450)' }}>
                Service: {tenant.ecs_service_arn?.split('/').pop() || 'N/A'}
              </div>
            </Space>
          ) : (
            <Tag color="orange">Provisioning...</Tag>
          )}
        </Descriptions.Item>

        <Descriptions.Item label={<Space><DatabaseOutlined /> RDS Instance</Space>} span={2}>
          {loadingRdsStatus ? (
            <Spin size="small" />
          ) : rdsStatus ? (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div>
                <strong>Instance Class:</strong> {rdsStatus.current_instance_class}
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  {getCostEstimate(rdsStatus.current_instance_class)}/month
                </Tag>
              </div>

              {rdsStatus.pending_instance_class && (
                <Alert
                  message={`Upgrading to ${rdsStatus.pending_instance_class}`}
                  description="This upgrade will be applied during the next maintenance window or is currently in progress."
                  type="info"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}

              {rdsStatus.rds_status && rdsStatus.rds_status !== 'available' && (
                <div>
                  <strong>Status:</strong> {getStatusTag(rdsStatus.rds_status)}
                </div>
              )}

              {rdsStatus.available_upgrades?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong>Available Upgrades:</strong>{' '}
                  {rdsStatus.available_upgrades.map((cls, idx) => (
                    <Tag key={cls} color="green">
                      {cls} ({getCostEstimate(cls)}/month)
                    </Tag>
                  ))}
                </div>
              )}

              {rdsStatus.available_upgrades?.length === 0 && !rdsStatus.is_upgrading && (
                <Tag color="default">No upgrades available (already at maximum)</Tag>
              )}

              <div style={{ fontSize: '12px', color: 'var(--rlc-neutral-450)', marginTop: 8 }}>
                Endpoint: {tenant.rds_endpoint?.split(':')[0] || 'N/A'}
              </div>
            </Space>
          ) : tenant.rds_endpoint ? (
            <Space direction="vertical" size="small">
              <div>
                <strong>Instance Class:</strong> {tenant.rds_instance_class || 'db.t3.micro'}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--rlc-neutral-450)' }}>
                Endpoint: {tenant.rds_endpoint.split(':')[0]}
              </div>
            </Space>
          ) : (
            <Tag color="orange">Provisioning...</Tag>
          )}
        </Descriptions.Item>

        <Descriptions.Item label={<Space><CloudOutlined /> S3 Bucket</Space>} span={2}>
          {tenant.s3_bucket_name ? (
            <div style={{ fontSize: '12px' }}>
              {tenant.s3_bucket_name}
            </div>
          ) : (
            <Tag color="orange">Provisioning...</Tag>
          )}
        </Descriptions.Item>
      </Descriptions>

      {/* RDS Upgrade Modal */}
      <Modal
        title={<Space><DatabaseOutlined /> Upgrade RDS Instance Class</Space>}
        open={upgradeModalVisible}
        onCancel={() => {
          setUpgradeModalVisible(false);
          setSelectedInstanceClass(null);
          setApplyImmediately(false);
        }}
        onOk={handleUpgradeRds}
        okText="Upgrade RDS"
        confirmLoading={upgrading}
        width={600}
      >
        <Alert
          message="RDS Instance Class Upgrade"
          description="Upgrading the RDS instance class will increase database performance (CPU, memory, IOPS). This is an in-place modification with minimal downtime."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {rdsStatus && (
          <>
            <div style={{ marginBottom: 16 }}>
              <strong>Current Instance Class:</strong>{' '}
              <Tag color="blue">{rdsStatus.current_instance_class}</Tag>
              <Tag color="blue">{getCostEstimate(rdsStatus.current_instance_class)}/month</Tag>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label>
                <strong>Target Instance Class:</strong>
              </label>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder="Select target instance class"
                value={selectedInstanceClass}
                onChange={setSelectedInstanceClass}
              >
                {rdsStatus.available_upgrades?.map((cls) => (
                  <Option key={cls} value={cls}>
                    <Space>
                      <span>{cls}</span>
                      <Tag color="green">{getCostEstimate(cls)}/month</Tag>
                      <span style={{ fontSize: '12px', color: 'var(--rlc-neutral-450)' }}>
                        (+${parseInt(getCostEstimate(cls).replace('$', '')) - parseInt(getCostEstimate(rdsStatus.current_instance_class).replace('$', ''))}/month)
                      </span>
                    </Space>
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label>
                <strong>Apply Timing:</strong>
              </label>
              <Radio.Group
                value={applyImmediately}
                onChange={(e) => setApplyImmediately(e.target.value)}
                style={{ width: '100%', marginTop: 8 }}
              >
                <Radio.Button value={false} style={{ width: '50%', textAlign: 'center' }}>
                  <div>
                    <div><strong>Next Maintenance Window</strong></div>
                    <div style={{ fontSize: '12px', color: 'var(--rlc-neutral-450)' }}>
                      Typically overnight (no downtime)
                    </div>
                  </div>
                </Radio.Button>
                <Radio.Button value={true} style={{ width: '50%', textAlign: 'center' }}>
                  <div>
                    <div><strong>Immediately</strong></div>
                    <div style={{ fontSize: '12px', color: 'var(--rlc-neutral-450)' }}>
                      10-15 minutes (brief downtime possible)
                    </div>
                  </div>
                </Radio.Button>
              </Radio.Group>
            </div>

            {selectedInstanceClass && (
              <Alert
                message="Cost Impact"
                description={
                  <div>
                    <div>
                      <strong>Current:</strong> {getCostEstimate(rdsStatus.current_instance_class)}/month
                    </div>
                    <div>
                      <strong>After Upgrade:</strong> {getCostEstimate(selectedInstanceClass)}/month
                    </div>
                    <div style={{ marginTop: 8, fontWeight: 'bold' }}>
                      <DollarOutlined /> Additional Cost: +${parseInt(getCostEstimate(selectedInstanceClass).replace('$', '')) - parseInt(getCostEstimate(rdsStatus.current_instance_class).replace('$', ''))}/month
                    </div>
                  </div>
                }
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </>
        )}
      </Modal>
    </Card>
  );
};

export default TenantComputeSettings;
